import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, forkJoin, fromEvent, of, firstValueFrom, catchError } from 'rxjs';
import moment from 'moment';
import { FieldExpression, NodeExpression, toString as luceneToString } from 'lucene';
import { ICellRendererParams, GridReadyEvent, ValueGetterParams, ViewportChangedEvent } from '@ag-grid-community/core';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgClass } from '@angular/common';

import { padSupplierOrderReference } from '../../../shared/utils/padding';
import { OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { UserService } from '../../../shared/services/user.service';
import { TranslationService } from '../../../shared/services/translation.service';
import { DocumentService } from '../../../commercial-documents/services/documents.service';
import {
	Document,
	DOCUMENTS_SUB_ENTITY,
	GlobalDocument,
	FieldOfDocument
} from '../../../commercial-documents/models/document';
import { User } from '../../../shared/models/user';
import {
	CommonColDef,
	DateColDef,
	IconRedirectColDef,
	LinkColDef,
	NumericColDef,
} from '../../../shared/datacenter/column-defs';
import { PaginatedResult } from '../../../shared/models/paginated-result';
import { ScheduleBusinessOrder, ScheduleBusinessOrderNeeds } from '../../models/schedule-business-order';
import { ScheduleBusinessOrderService } from '../../services/schedule-business-order.service';
import { CommonGridOptions, clientSideAgGridModules } from '../../../shared/datacenter/grid-options';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { ScheduleBusinessOrderBatch } from '../../models/schedule-business-order-batch';
import { isoWeekToMoment } from '../../../offer/models/iso-week-or-month-utils';
import { IsoWeek } from '../../../offer/models/month-week-value';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import {
	DetailedReportData,
	DetailedReportDialogComponent
} from '../../../shared/datacenter/types/schedule-business-orders/components/detailed-report-dialog.component';
import { CheckboxHeaderComponent } from '../../../shared/datacenter/header-components/checkbox-header.component';
import { DatacenterFilterType } from '../../../shared/models/datacenter-filters';
import { UserDatacenterView } from '../../../shared/models/datacenter-aggrid-view';
import { DatacenterDefaultConfiguration } from '../../../shared/datacenter/datacenter-default-configuration';
import { ModelInfoPipe } from '../../../shared/pipes/model-info.pipe';
import { CharacPipe } from '../../../shared/pipes/characteristic.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { OfflineComponent } from '../../../shared/components/offline/offline.component';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ScheduleBusinessOrderCharacteristic } from '../../models/schedule-business-order-characteristic';
import { BatchesService } from '../../services/batches.service';

import { NeedsIconCellComponent } from './needs-cell-renderer/needs-icon-cell.component';

interface SBOrderNeeds {
	title: string;
	twelveWeeksNeedsData: string[] | number[];
	unit: string;
	showIcon: boolean;
}

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'date';
const resizeObservable = fromEvent(globalThis, 'resize');
const LIMIT = 500;

const linkToDocumentCellRendererParams = {
	routerLink(params: ICellRendererParams<Document, unknown>) {
		if (!params.data) {
			return undefined;
		}
		const document = params.data;
		let documentType;
		switch (document.type) {
		case 'IN':
			documentType = 'invoice-card';
			break;
		case 'CE':
			documentType = 'certificate-card';
			break;
		case 'DA':
			documentType = 'dispatch-advice-card';
			break;
		case 'OA':
			documentType = 'order-acknowledgment-card';
			break;
		default:
			throw new Error('unknown document type');
		}

		return {
			commands: [
				'/commercial-documents',
				'cards',
				documentType,
				document.id
			],
		};
	}
};

const linkToBatchCardCellRendererParams = {
	routerLink(params: ICellRendererParams<ScheduleBusinessOrderBatch, unknown>) {
		const batch = params.data;
		if (!batch) {
			return undefined;
		}
		return {
			commands: [
				'/order-tracking',
				'batch',
				'card',
				batch.supplierBatchReference
			]
		};
	}
};

@Component({
	selector: 'copo-sb-order-card',
	templateUrl: './schedule-business-order-card.component.html',
	styleUrl: './schedule-business-order-card.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		BackButtonComponent,
		TranslateModule,

		MatButtonModule,
		MatIconModule,
		MatExpansionModule,
		MatDividerModule,
		NgClass,
		AgGridModule,
		AfterViewInitDirective,
		OfflineComponent,
		LoadingSpinnerComponent,
		NumberPipe,
		CharacPipe,
		ModelInfoPipe
	]
})
export class ScheduleBusinessOrderCardComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	gridCommercialDocumentsOptions?: CommonGridOptions<GlobalDocument>;
	user: User;
	isLoading = false;
	isOffline = false;
	isSAOrderNotFound = false;
	isCommercialDocumentFragment = false;
	invoiceDisplayed = false;
	certificateDisplayed = false;
	dispatchAdviceDisplayed = false;
	orderAcknowledgmentDisplayed = false;
	hasCommercialDocumentError = false;
	supplierOrderNumber: string;
	overlayNoDocumentsTemplate?: string;
	documentResult: PaginatedResult<Document> | undefined;
	scheduleBusinessOrder?: ScheduleBusinessOrder;
	scheduleBusinessOrderNeeds?: ScheduleBusinessOrderNeeds;
	scheduleBusinessOrderBatches?: ScheduleBusinessOrderBatch[];
	blockedBatches?: ScheduleBusinessOrderBatch[];
	deliveredBatches?: ScheduleBusinessOrderBatch[];
	readyToShipBatches?: ScheduleBusinessOrderBatch[];
	landTransitBatches?: ScheduleBusinessOrderBatch[];
	atReshipperBatches?: ScheduleBusinessOrderBatch[];
	onVesselBatches?: ScheduleBusinessOrderBatch[];
	overseasTransitBatches?: ScheduleBusinessOrderBatch[];
	transitToCustomerBatches?: ScheduleBusinessOrderBatch[];
	consignmentStockBatches?: ScheduleBusinessOrderBatch[];
	batchesSubscription?: Subscription;
	gridNeedsOptions?: CommonGridOptions<SBOrderNeeds>;
	resizeSub?: Subscription;
	sbOrderNeeds?: SBOrderNeeds[];
	needsArray: SBOrderNeeds[] = [];
	globalDocuments: GlobalDocument[] = [];
	documentsFields: (FieldOfDocument)[] = [
		'id',
		'subtype',
		'date',
		'name',
		'type',
		'proforma'
	];

	rowDataLandTransitBatches?: ScheduleBusinessOrderBatch[];
	readonly agGridModules = clientSideAgGridModules;
	readonly LAND_TRANSIT_STATUS = ['11', '06', '16', '41'];
	readonly AT_RESHIPPER_STATUS = ['13'];
	readonly ON_VESSEL_STATUS = ['21', '26', '27', '22', '25'];
	readonly OVERSEAS_TRANSIT_STATUS = ['34', '31', '33', '36', '37'];
	readonly TRANSIT_TO_CUSTOMER_STATUS = ['15', '12', '42', '43'];
	readonly CONSIGNMENT_STOCK_STATUS = ['07', '38'];

	gridBatchBlockedOptions?: CommonGridOptions<ScheduleBusinessOrderBatch>;
	gridBatchDeliveredOptions?: CommonGridOptions<ScheduleBusinessOrderBatch>;
	gridBatchReadyToShipOptions?: CommonGridOptions<ScheduleBusinessOrderBatch>;
	gridBatchLandTransitOptions?: CommonGridOptions<ScheduleBusinessOrderBatch>;
	gridBatchAtReshipperOptions?: CommonGridOptions<ScheduleBusinessOrderBatch>;
	gridBatchOverseasTransitOptions?: CommonGridOptions<ScheduleBusinessOrderBatch>;
	gridBatchConsignmentStockOptions?: CommonGridOptions<ScheduleBusinessOrderBatch>;
	gridInProductionOptions?: CommonGridOptions<ScheduleBusinessOrderBatch>;
	gridBatchOnVesselOptions?: CommonGridOptions<ScheduleBusinessOrderBatch>;
	gridBatchTransitToCustomerOptions?: CommonGridOptions<ScheduleBusinessOrderBatch>;

	sumGrossBlockedBatches = 0;
	sumNetBlockedBatches = 0;

	sumGrossDeliveredBatches = 0;
	sumNetDeliveredBatches = 0;
	sumGrossAvailableBatches = 0;
	sumNetAvailableBatches = 0;

	sumGrossReadyToShipBatches = 0;
	sumNetReadyToShipBatches = 0;

	sumGrossLandTransitBatches = 0;
	sumNetLandTransitBatches = 0;

	sumGrossAtReshipperBatches = 0;
	sumNetAtReshipperBatches = 0;

	sumGrossOnVesselBatches = 0;
	sumNetOnVesselBatches = 0;

	sumGrossOverseasTransitBatches = 0;
	sumNetOverseasTransitBatches = 0;

	sumGrossTransitToCustomerBatches = 0;
	sumNetTransitToCustomerBatches = 0;

	sumGrossConsignmentStockBatches = 0;
	sumNetConsignmentStockBatches = 0;

	dateFormat: DatePipe;
	characteristicsResult?: PaginatedResult<ScheduleBusinessOrderCharacteristic>;
	orderCharacteristics?: ScheduleBusinessOrderCharacteristic[];

	private readonly id?: string;
	private readonly previousState: Record<string, unknown> | undefined;

	constructor(
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		private readonly commercialDocumentService: DocumentService,
		private readonly scheduleBusinessOrderService: ScheduleBusinessOrderService,

		private readonly dialog: MatDialog,
		private readonly defaultConfiguration: DatacenterDefaultConfiguration,
		private readonly batchservice: BatchesService
	) {
		this.dateFormat = new DatePipe();
		this.supplierOrderNumber = <string> this.route.snapshot.params['supplierOrderNumber'];
		this.isOffline = !navigator.onLine;
		this.isLoading = true;
		this.user = this.userService.user;
		this.isCommercialDocumentFragment = route.snapshot.fragment === 'documents';
		this.user.hasServiceParameterEqualsTo('PDOCS', 'OrderAcknowledgment', 'True');
		this.overlayNoDocumentsTemplate = this.translationService.getTR('order.no-documents');
		this.invoiceDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'Invoice', 'True');
		this.certificateDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'Certificate', 'True');
		this.dispatchAdviceDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'DispatchAdvice', 'True');
		this.orderAcknowledgmentDisplayed =
			this.user.hasServiceParameterEqualsTo('PDOCS', 'OrderAcknowledgment', 'True');
		this.getOrderData()
			.then(orders => {
				if (Object.keys(orders[0]).length === 0) {
					this.isLoading = false;
					this.isSAOrderNotFound = true;
				}
				this.scheduleBusinessOrder = orders[0];
				this.scheduleBusinessOrderNeeds = orders[1].count > 1 ? orders[1].results[0] : undefined;
				this.documentResult = orders[2];
				this.characteristicsResult = orders[3];
			}, (err: unknown) => {
				console.error('error loading', err);
				this.isLoading = false;
			})
			.then(() => {
				this.constructBlockedGrid();
				this.constructReadyToShipBatchesGrid();
				this.constructLandTransitGrid();
				this.constructBatchesAtReshipperGrid();
				this.constructOnVesselBatchesGrid();
				this.constructOverseasTransitGrid();
				this.constructTransitToCustomerBatches();
				this.constructConsignmentStockBatches();
				this.sumGrossAvailableBatches = this.sumGrossReadyToShipBatches
					+ this.sumGrossLandTransitBatches
					+ this.sumGrossAtReshipperBatches + this.sumGrossOnVesselBatches
					+ this.sumGrossOverseasTransitBatches + this.sumGrossTransitToCustomerBatches
					+ this.sumGrossConsignmentStockBatches;
				this.sumNetAvailableBatches = this.sumNetReadyToShipBatches + this.sumNetLandTransitBatches
					+ this.sumNetAtReshipperBatches + this.sumNetOnVesselBatches
					+ this.sumNetOverseasTransitBatches + this.sumNetTransitToCustomerBatches
					+ this.sumNetConsignmentStockBatches;

				this.constructDeliveredGrid();
				this.constructSBOrdersNeeds();
				this.constructCommercialDocuments();
				this.constructDeliveredGridTotalQty();
				this.orderCharacteristics = this.characteristicsResult?.results;
				this.isLoading = false;
			}).catch((err: unknown) => {
				console.error(err);
			});
	}

	onGridReady(params: GridReadyEvent) {
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	autoScrollToElement(documentEl: HTMLElement) {
		if (this.id === 'documents') {
			documentEl.scrollIntoView();
		}
	}

	ngOnDestroy() {
		if (this.resizeSub !== undefined) {
			this.resizeSub.unsubscribe();
		}
	}

	sumGross(batches: ScheduleBusinessOrderBatch[]) {
		return batches.reduce((previous, current) => previous + current.qtyBatchGross, 0);
	}

	sumNet(batches: ScheduleBusinessOrderBatch[]) {
		return batches.reduce((previous, current) => previous + current.qtyBatchNet, 0);
	}

	getCharacteristic(code: string) {
		return this.orderCharacteristics?.find(c => c.code === code);
	}

	getColumnDefs() {
		const allColumns = [];
		if (this.scheduleBusinessOrderNeeds) {
			const title = new CommonColDef<SBOrderNeeds>({
				valueGetter: params => {
					const { data } = params;
					if (!data || data.title === 'icon') {
						return '';
					}
					return data.title;
				},
				cellRenderer: NeedsIconCellComponent,
			});
			allColumns.push(title);
			for (let i = 0; i < this.scheduleBusinessOrderNeeds.needsForTwelveWeeks.needs.length; i++) {
				const needs = new CommonColDef<SBOrderNeeds>({
					valueGetter: params => {
						const paramsData = params.data;
						if (!paramsData) {
							return '';
						}
						if (this.scheduleBusinessOrderNeeds && paramsData.title === 'icon' && i > 0) {
							return this.getRealizedCumulIcon(
								this.scheduleBusinessOrderNeeds.stockForTwelveWeeks.needs[i - 1],
								this.scheduleBusinessOrderNeeds.stockForTwelveWeeks.needs[i]
							);
						}
						return paramsData.twelveWeeksNeedsData[i];
					},
					cellRenderer: NeedsIconCellComponent,
				});
				allColumns.push(needs);
			}
		}
		return allColumns;
	}

	detailedReport() {
		const supplierOrderNumbers: string[] = [];
		supplierOrderNumbers.push(this.supplierOrderNumber);
		this.dialog.open<DetailedReportDialogComponent, DetailedReportData, string | undefined>(
			DetailedReportDialogComponent,
			{
				panelClass: 'save-view-block',
				data: {
					id: supplierOrderNumbers
				},
				disableClose: true,
			}
		);
	}

	async navigation(defaultView: UserDatacenterView, routePath: string) {
		await this.router.navigate([routePath], {
			relativeTo: this.route,
			state: { ...this.previousState, view: defaultView }
		});
	}

	async close(defaultView: UserDatacenterView) {
		let route = '';
		if (defaultView.type === 'batch') {
			route = '/order-tracking/batch/datacenter';
		}
		if (defaultView.type === 'document') {
			route = '/commercial-documents/datacenter';
		}
		await this.navigation(defaultView, route);
	}

	async seeAllBatches() {
		const defaultView = this.defaultConfiguration.getDefaultView('batch');
		const filtersList: DatacenterFilterType[] = [];
		const statusGroupFilter: DatacenterFilterType = {
			id: 'statusGroup',
			values: [
				{
					code: '5',
					translation: 'Delivered',
				}
			]
		};
		const sBOrderNumberFilter: DatacenterFilterType = {
			id: 'supplierOrderNumber',
			values: [
				{
					reference: this.supplierOrderNumber,
				}
			]
		};
		filtersList.push(statusGroupFilter, sBOrderNumberFilter);
		defaultView.filterState = filtersList;
		await this.close(defaultView);
	}

	async seeAllDocuments() {
		const defaultView = this.defaultConfiguration.getDefaultView('document');
		const filtersList: DatacenterFilterType[] = [];
		const supplierOrderNumberValues = [];
		supplierOrderNumberValues.push(
			{
				reference: this.supplierOrderNumber,
				entityReferenceName: 'documentLines'
			}
		);
		if (this.supplierOrderNumber.startsWith('0')) {
			supplierOrderNumberValues.push(
				{
					reference: this.supplierOrderNumber.replace(/^0+/, ''),
					entityReferenceName: 'documentLines'
				}
			);
		} else {
			supplierOrderNumberValues.push(
				{
					reference: `0${ this.supplierOrderNumber }`,
					entityReferenceName: 'documentLines'
				}
			);
		}
		const sBOrderNumberFilter: DatacenterFilterType = {
			id: 'supplierOrderNumber',
			values: supplierOrderNumberValues
		};
		filtersList.push(sBOrderNumberFilter);
		defaultView.filterState = filtersList;
		await this.close(defaultView);
	}

	private constructCommercialDocuments() {
		if (this.documentResult) {
			for (const document of this.documentResult.results) {
				const globalDocument: GlobalDocument = {
					title: document.type === 'IN' ? this.translationService.getTR('order.card.document.invoices')
						: document.type === 'DA' ? this.translationService.getTR('order.card.document.dispatch.advices')
							: document.type === 'CE' ? this.translationService.getTR('order.card.document.certificates')
								: this.translationService.getTR('order.card.document.order.acknowledgments'),
					name: document.name,
					date: document.date,
					id: document.id,
					subtype: document.subtypeDescription,
					type: document.type,
					proforma: document.proforma
				};

				this.globalDocuments.push(globalDocument);
			}
		}
		this.constructGridCommercialDocumentsOptions(this.globalDocuments);
	}

	private constructGridCommercialDocumentsOptions(globalDocuments: GlobalDocument[]) {
		this.gridCommercialDocumentsOptions = new CommonGridOptions<GlobalDocument>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			rowData: globalDocuments,
			headerHeight: 60,

			columnDefs: [
				new CommonColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.document'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'title',
				}),
				new CommonColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.type'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'subtype',
				}),
				...globalDocuments.some(d => d.proforma === 'true') ? [
					new CommonColDef<GlobalDocument>({
						headerName: this.translationService.getTR('order-card.proforma'),
						headerComponent: CheckboxHeaderComponent,
						field: 'proforma',
						sortable: true,
						valueFormatter: params => {
							if (params.value === 'true') {
								return this.translationService.getTR('global.yes');
							}
							if (params.value === 'false') {
								return this.translationService.getTR('global.no');
							}
							return '';
						}
					})
				] : [],
				new LinkColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.name'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'name',
					cellRendererParams: linkToDocumentCellRendererParams
				}),
				new DateColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order.creation.date'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					sort: 'desc',
					field: 'date',
					width: 100,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' }
				}),
				new IconRedirectColDef<GlobalDocument>({
					field: 'name',
					cellRendererParams: {
						linkData: (params: ICellRendererParams<GlobalDocument, unknown>) => {
							const { data } = params;
							if (!data) {
								return undefined;
							}
							return {
								url: this.commercialDocumentService
									.getDownloadDocumentUrl(data.id, data.name),
								type: 'application/pdf',
								icon: 'table_document'
							};
						}
					}
				})
			]
		});
	}

	private constructDocumentFilter() {
		const supplierOrderReferenceFieldFromDoc: FieldExpression = {
			field: 'supplierOrderReference',
			term: padSupplierOrderReference(this.supplierOrderNumber),
			quoted: true
		};
		const supplierOrderReferenceFieldFromDocLine: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.supplierOrderReference`,
			term: padSupplierOrderReference(this.supplierOrderNumber),
			quoted: true
		};
		const supplierOrderNumberNode: NodeExpression = {
			left: supplierOrderReferenceFieldFromDoc,
			operator: 'OR',
			right: supplierOrderReferenceFieldFromDocLine,
			parenthesized: true
		};
		return luceneToString(supplierOrderNumberNode);
	}

	private async getOrderData() {
		return firstValueFrom(this.getObservableForCard())
			.catch(async () => {
				if (!this.supplierOrderNumber || this.supplierOrderNumber.trim().length === 0) {
					throw new Error('invalid supplier reference');
				}
				this.supplierOrderNumber = this.supplierOrderNumber.startsWith('0')
					? this.supplierOrderNumber.slice(1)
					: `0${ this.supplierOrderNumber }`;
				return firstValueFrom(this.getObservableForCard());
			});
	}

	private getObservableForCard() {
		return forkJoin([
			this.scheduleBusinessOrderService.getScheduleBusinessOrderCard(
				this.supplierOrderNumber,
				undefined
			),
			this.scheduleBusinessOrderService.getScheduleBusinessNeeds(
				this.supplierOrderNumber
			),
			this.invoiceDisplayed || this.dispatchAdviceDisplayed
			|| this.orderAcknowledgmentDisplayed || this.certificateDisplayed
				? this.commercialDocumentService.getDocuments(
					this.documentsFields,
					this.constructDocumentFilter(),
					0,
					10,
					new OrderBy(SORT_BY, SORT_WAY),
					false
				).pipe(
					catchError((err: unknown) => {
						console.error(err);
						this.hasCommercialDocumentError = true;
						return of(undefined);
					})
				)
				: of(undefined),
			this.scheduleBusinessOrderService.getSBOrderCharacteritics(
				this.supplierOrderNumber
			)
		]);
	}

	private constructSBOrdersNeeds() {
		this.createNeedsGridDataArray();
		this.gridNeedsOptions = new CommonGridOptions<SBOrderNeeds>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: this.needsArray,
			domLayout: 'autoHeight',
			columnDefs: this.getColumnDefs()
		});
	}

	private createNeedsGridDataArray() {
		if (!this.scheduleBusinessOrderNeeds) {
			return;
		}
		const weeks: string[] = this.getWeeks(this.scheduleBusinessOrderNeeds.needsForTwelveWeeks.week);
		this.needsArray.push(
			{
				title: this.translationService.getTR('schedule-business-order.needs'),
				twelveWeeksNeedsData: this.scheduleBusinessOrderNeeds.needsForTwelveWeeks.needs,
				unit: this.scheduleBusinessOrderNeeds.weightUnit,
				showIcon: false,
			},
			{
				title: this.translationService.getTR('schedule-business-order.wip'),
				twelveWeeksNeedsData: this.scheduleBusinessOrderNeeds.wipForTwelveWeeks.needs,
				unit: this.scheduleBusinessOrderNeeds.weightUnit,
				showIcon: false,
			},
			{
				title: this.translationService.getTR('schedule-business-order.stock'),
				twelveWeeksNeedsData: this.scheduleBusinessOrderNeeds.stockForTwelveWeeks.needs,
				unit: this.scheduleBusinessOrderNeeds.weightUnit,
				showIcon: false,
			},
			{
				title: '',
				twelveWeeksNeedsData: weeks,
				unit: '',
				showIcon: false,
			},
			{
				title: this.translationService.getTR('schedule-business-order.icon'),
				twelveWeeksNeedsData: [],
				unit: '',
				showIcon: true,
			}
		);
	}

	private getRealizedCumulIcon(prev: number, cur: number) {
		return prev < cur ? 'icon_cumul_green' : prev > cur ? 'icon_cumul_red' : '';
	}

	private getWeeks(week: string): string[] {
		const weekNumberArr = week.split('-');
		const year = Number(weekNumberArr[0]);
		const weekNumber = Number(weekNumberArr[1].slice(1));
		const totalNumberOfWeeks = moment({ year, month: 0, date: 1 }).isoWeeksInYear();
		const momentStart = isoWeekToMoment(<IsoWeek>{ value: weekNumber, year });
		const nextYear = year + 1;
		const weeks = [`W${ String(weekNumber) } - ${ String(year) }`];
		if (weekNumber === totalNumberOfWeeks) {
			for (let i = 1; i < 12; i++) {
				weeks.push(`W${ String(i) } - ${ String(nextYear) }`);
			}
		} else if (momentStart.month() >= 10 && weekNumber >= 41) {
			for (let i = weekNumber + 1; i <= totalNumberOfWeeks; i++) {
				weeks.push(`W${ String(i) } - ${ String(year) }`);
			}
			if (weeks.length < 12) {
				const weekLength = weeks.length;
				for (let i = 1; i <= 12 - weekLength; i++) {
					weeks.push(`W${ String(i) } - ${ String(nextYear) }`);
				}
			}
		} else {
			for (let i = 1; i < 12; i++) {
				weeks.push(`W${ String(weekNumber + i) } - ${ String(year) }`);
			}
		}
		return weeks;
	}

	private constructReadyToShipBatchesGrid() {
		const readyToShipStatusGroup = 'batchStatusGroup:2';
		this.batchesSubscription = this.scheduleBusinessOrderService.getBatchesByScheduleBusinessOrderKey(
			this.supplierOrderNumber,
			readyToShipStatusGroup,
			LIMIT
		).subscribe(data => {
			this.readyToShipBatches = data.results;
			this.sumGrossReadyToShipBatches = this.sumGross(this.readyToShipBatches);
			this.sumNetReadyToShipBatches = this.sumNet(this.readyToShipBatches);
			this.gridBatchReadyToShipOptions = new CommonGridOptions<ScheduleBusinessOrderBatch>({
				translationService: this.translationService,
				multiSortKey: 'ctrl',
				rowData: this.readyToShipBatches,
				domLayout: 'autoHeight',
				columnDefs: [
					new LinkColDef<ScheduleBusinessOrderBatch>({
						headerName: this.translationService.getTR('batch.reference'),
						field: 'batchReference',
						cellRendererParams: linkToBatchCardCellRendererParams
					}),
					new NumericColDef<ScheduleBusinessOrderBatch>({
						headerName: this.translationService.getTR('batch.gross-t'),
						field: 'qtyBatchGross',
						cellRendererParams: { numericFormat: 'weight-with-zero' }
					}),
					new NumericColDef<ScheduleBusinessOrderBatch>({
						headerName: this.translationService.getTR('batch.net-t'),
						field: 'qtyBatchNet',
						cellRendererParams: { numericFormat: 'weight-with-zero' }
					}),
					new CommonColDef<ScheduleBusinessOrderBatch>({
						headerName: this.translationService.getTR('batch.detailed-batch-status'),
						field: 'batchStatusDescription'
					}),
					new DateColDef<ScheduleBusinessOrderBatch>({
						headerName: this.translationService.getTR('batch.date-for-dispatch'),
						field: 'availableInStockDate'
					}),
					new DateColDef<ScheduleBusinessOrderBatch>({
						headerName: this.translationService.getTR('batch.planned-loading-date'),
						field: 'plannedShipmentDate'
					}),
					new CommonColDef<ScheduleBusinessOrderBatch>({
						headerName: this.translationService.getTR('batch.current-location'),
						field: 'currentLocationDescription'
					}),
				]
			});
		});
	}

	private constructBlockedGrid() {
		const statusGroup = 'batchStatusGroup:1';
		this.batchesSubscription = this.scheduleBusinessOrderService
			.getBatchesByScheduleBusinessOrderKey(this.supplierOrderNumber, statusGroup, LIMIT)
			.subscribe(data => {
				this.blockedBatches = data.results;
				this.sumGrossBlockedBatches = this.sumGross(this.blockedBatches);
				this.sumNetBlockedBatches = this.sumNet(this.blockedBatches);
				this.gridBatchBlockedOptions = new CommonGridOptions<ScheduleBusinessOrderBatch>({
					translationService: this.translationService,
					multiSortKey: 'ctrl',
					rowData: this.blockedBatches,
					domLayout: 'autoHeight',
					columnDefs: [
						new LinkColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.reference'),
							field: 'batchReference',
							cellRendererParams: linkToBatchCardCellRendererParams
						}),
						new NumericColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.gross-t'),
							field: 'qtyBatchGross',
							cellRendererParams: { numericFormat: 'weight-with-zero' }
						}),
						new NumericColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.net-t'),
							field: 'qtyBatchNet',
							cellRendererParams: { numericFormat: 'weight-with-zero' }
						}),
						new CommonColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.detailed-batch-status'),
							field: 'batchStatusDescription'
						}),
						new CommonColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.current-location'),
							field: 'currentLocationDescription'
						}),
					]
				});
			});
	}

	private constructDeliveredGrid() {
		const statusGroup = 'batchStatusGroup:5';
		this.batchesSubscription = this.scheduleBusinessOrderService
			.getBatchesByScheduleBusinessOrderKey(this.supplierOrderNumber, statusGroup, 10)
			.subscribe(batchesData => {
				this.deliveredBatches = batchesData.results;
				for (const data of this.deliveredBatches) {
					if (this.comparePlannedShipmentDate(data.plannedShipmentDate)) {
						this.deliveredBatches.splice(this.deliveredBatches.indexOf(data), 1);
					}
				}
				this.gridBatchDeliveredOptions = new CommonGridOptions<ScheduleBusinessOrderBatch>({
					translationService: this.translationService,
					multiSortKey: 'ctrl',
					rowData: this.deliveredBatches,
					domLayout: 'autoHeight',
					columnDefs: [
						new LinkColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.reference'),
							field: 'batchReference',
							cellRendererParams: linkToBatchCardCellRendererParams
						}),
						new NumericColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.gross-t'),
							field: 'qtyBatchGross',
							cellRendererParams: { numericFormat: 'weight-with-zero' }
						}),
						new NumericColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.net-t'),
							field: 'qtyBatchNet',
							cellRendererParams: { numericFormat: 'weight-with-zero' }
						}),
						new DateColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.date-for-dispatch'),
							field: 'availableInStockDate'
						}),
						new CommonColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('shipment.transport-reference'),
							valueGetter: params => this.getTransportReference(params)
						}),
						new CommonColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('order.transport-mode'),
							valueGetter: params => this.getTransportModeDescription(params)
						}),
						new DateColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('shipment.dispatch-date'),
							field: 'dispatchAdviceDate'
						}),
						new CommonColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('shipment.dispatch-advice'),
							field: 'dispatchAdviceNumber'
						}),
						new CommonColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('shipment.to'),
							field: 'destinationDescription'
						}),
					]
				});
			});
	}

	private constructDeliveredGridTotalQty() {
		const statusGroup = 'batchStatusGroup:5';
		const distinct = true;
		const fields = ['qtyBatchGross', 'qtyBatchNet'];
		const orderBy = 'qtyBatchGross asc';
		this.batchesSubscription = this.scheduleBusinessOrderService
			.getTotalQuantity(this.supplierOrderNumber, statusGroup, distinct, fields, orderBy)
			.subscribe(batchesData => {
				this.sumGrossDeliveredBatches =
					batchesData.results.length > 0 ? batchesData.results[0].qtyBatchGross : 0;
				this.sumNetDeliveredBatches =
					batchesData.results.length > 0 ? batchesData.results[0].qtyBatchNet : 0;
			});
	}

	private comparePlannedShipmentDate(date: string) {
		const plannedShipmentDate = this.dateFormat.transform(date, 'date');
		const sixMonths = this.dateFormat.transform(moment().subtract(6, 'months'), 'date');
		return moment(plannedShipmentDate).isBefore(sixMonths);
	}

	private getTransportReference(params: ValueGetterParams) {
		const batch = <ScheduleBusinessOrderBatch> params.data;
		return batch.shipments.length > 0
			? batch.shipments[batch.shipments.length - 1].transportReference
			: '';
	}

	private getTransportModeDescription(params: ValueGetterParams) {
		const batch = <ScheduleBusinessOrderBatch> params.data;
		return batch.shipments.length > 0
			? batch.shipments[batch.shipments.length - 1].transportModeDescription
			: '';
	}

	private gridLandVesselCustomerFields(batches: ScheduleBusinessOrderBatch[]) {
		return new CommonGridOptions<ScheduleBusinessOrderBatch>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: batches,
			domLayout: 'autoHeight',
			columnDefs: [
				new LinkColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('batch.reference'),
					field: 'batchReference',
					cellRendererParams: linkToBatchCardCellRendererParams
				}),
				new NumericColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('batch.gross-t'),
					field: 'qtyBatchGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new NumericColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('batch.net-t'),
					field: 'qtyBatchNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new CommonColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('batch.detailed-batch-status'),
					field: 'batchStatusDescription'
				}),
				new DateColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('batch.date-for-dispatch'),
					field: 'availableInStockDate'
				}),
				new CommonColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('shipment.transport-reference'),
					valueGetter: params => this.getTransportReference(params)
				}),
				new CommonColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('order.transport-mode'),
					valueGetter: params => this.getTransportModeDescription(params)
				}),
				new DateColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('shipment.dispatch-date'),
					field: 'dispatchAdviceDate'
				}),
				new CommonColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('shipment.dispatch-advice'),
					field: 'dispatchAdviceNumber'
				}),
				new CommonColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('shipment.to'),
					field: 'destinationDescription'
				}),
			]
		});
	}

	private gridConsignmentOverseasFields(batches: ScheduleBusinessOrderBatch[]) {
		return new CommonGridOptions<ScheduleBusinessOrderBatch>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: batches,
			domLayout: 'autoHeight',
			columnDefs: [
				new LinkColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('batch.reference'),
					field: 'batchReference',
					cellRendererParams: linkToBatchCardCellRendererParams
				}),
				new NumericColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('batch.gross-t'),
					field: 'qtyBatchGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new NumericColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('batch.net-t'),
					field: 'qtyBatchNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new CommonColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('batch.detailed-batch-status'),
					field: 'batchStatusDescription'
				}),
				new DateColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('batch.date-for-dispatch'),
					field: 'availableInStockDate'
				}),
				new CommonColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('batch.current-location'),
					field: 'currentLocationDescription'
				}),
				new CommonColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('shipment.transport-reference'),
					valueGetter: params => this.getTransportReference(params)
				}),
				new CommonColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('order.transport-mode'),
					valueGetter: params => this.getTransportModeDescription(params)
				}),
				new DateColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('shipment.dispatch-date'),
					field: 'dispatchAdviceDate'
				}),
				new CommonColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('shipment.dispatch-advice'),
					field: 'dispatchAdviceNumber'
				}),
				new CommonColDef<ScheduleBusinessOrderBatch>({
					headerName: this.translationService.getTR('shipment.to'),
					field: 'destinationDescription'
				}),
			]
		});
	}

	private constructLandTransitGrid() {
		const filterString = this.batchservice.constructLandTransitBatchesFilter();
		this.batchesSubscription = this.scheduleBusinessOrderService
			.getBatchesByScheduleBusinessOrderKey(this.supplierOrderNumber, filterString, LIMIT)
			.subscribe(data => {
				this.landTransitBatches = data.results;
				this.sumGrossLandTransitBatches = this.sumGross(this.landTransitBatches);
				this.sumNetLandTransitBatches = this.sumNet(this.landTransitBatches);
				this.gridBatchLandTransitOptions = this.gridLandVesselCustomerFields(this.landTransitBatches);
			});
	}

	private constructBatchesAtReshipperGrid() {
		const filterString = this.batchservice.constructAtReshipperBatchesFilter();
		this.batchesSubscription = this.scheduleBusinessOrderService
			.getBatchesByScheduleBusinessOrderKey(this.supplierOrderNumber, filterString, LIMIT)
			.subscribe(data => {
				this.atReshipperBatches = data.results;
				this.sumGrossAtReshipperBatches = this.sumGross(this.atReshipperBatches);
				this.sumNetAtReshipperBatches = this.sumNet(this.atReshipperBatches);
				this.gridBatchAtReshipperOptions = new CommonGridOptions<ScheduleBusinessOrderBatch>({
					translationService: this.translationService,
					multiSortKey: 'ctrl',
					rowData: this.atReshipperBatches,
					domLayout: 'autoHeight',
					columnDefs: [
						new LinkColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.reference'),
							field: 'batchReference',
							cellRendererParams: linkToBatchCardCellRendererParams
						}),
						new NumericColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.gross-t'),
							field: 'qtyBatchGross',
							cellRendererParams: { numericFormat: 'weight-with-zero' }
						}),
						new NumericColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.net-t'),
							field: 'qtyBatchNet',
							cellRendererParams: { numericFormat: 'weight-with-zero' }
						}),
						new CommonColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.detailed-batch-status'),
							field: 'batchStatusDescription'
						}),
						new DateColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.date-for-dispatch'),
							field: 'availableInStockDate'
						}),
						new DateColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.planned-loading-date'),
							field: 'plannedShipmentDate'
						}),
						new CommonColDef<ScheduleBusinessOrderBatch>({
							headerName: this.translationService.getTR('batch.current-location'),
							field: 'currentLocationDescription'
						}),
					]
				});
			});
	}

	private constructOnVesselBatchesGrid() {
		const filterString = this.batchservice.constructOnVesselBatchesFilter();
		this.batchesSubscription = this.scheduleBusinessOrderService
			.getBatchesByScheduleBusinessOrderKey(this.supplierOrderNumber, filterString, LIMIT)
			.subscribe(data => {
				this.onVesselBatches = data.results;
				this.sumGrossOnVesselBatches = this.sumGross(this.onVesselBatches);
				this.sumNetOnVesselBatches = this.sumNet(this.onVesselBatches);
				this.gridBatchOnVesselOptions = this.gridLandVesselCustomerFields(this.onVesselBatches);
			});
	}

	private constructOverseasTransitGrid() {
		const filterString = this.batchservice.constructOverseasTransitBatchesFilter();
		this.batchesSubscription = this.scheduleBusinessOrderService.getBatchesByScheduleBusinessOrderKey(
			this.supplierOrderNumber,
			filterString,
			LIMIT
		).subscribe(data => {
			this.overseasTransitBatches = data.results;
			this.sumGrossOverseasTransitBatches = this.sumGross(this.overseasTransitBatches);
			this.sumNetOverseasTransitBatches = this.sumNet(this.overseasTransitBatches);
			this.gridBatchOverseasTransitOptions =
			this.gridConsignmentOverseasFields(this.overseasTransitBatches);
		});
	}

	private constructTransitToCustomerBatches() {
		const filterString = this.batchservice.constructTransitToCustomerBatchesFilter();
		this.batchesSubscription = this.scheduleBusinessOrderService.getBatchesByScheduleBusinessOrderKey(
			this.supplierOrderNumber,
			filterString,
			LIMIT
		).subscribe(data => {
			this.transitToCustomerBatches = data.results;
			this.sumGrossTransitToCustomerBatches = this.sumGross(this.transitToCustomerBatches);
			this.sumNetTransitToCustomerBatches = this.sumNet(this.transitToCustomerBatches);
			this.gridBatchTransitToCustomerOptions =
					this.gridLandVesselCustomerFields(this.transitToCustomerBatches);
		});
	}

	private constructConsignmentStockBatches() {
		const filterString = this.batchservice.constructConsignmentStockBatchesFilter();
		this.batchesSubscription = this.scheduleBusinessOrderService.getBatchesByScheduleBusinessOrderKey(
			this.supplierOrderNumber,
			filterString,
			LIMIT
		).subscribe(data => {
			this.consignmentStockBatches = data.results;
			this.sumGrossConsignmentStockBatches = this.sumGross(this.consignmentStockBatches);
			this.sumNetConsignmentStockBatches = this.sumNet(this.consignmentStockBatches);
			this.gridBatchConsignmentStockOptions =
				this.gridConsignmentOverseasFields(this.consignmentStockBatches);
		});
	}
}
